import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VendorTable = ({ vendors, onEditVendor, onViewVendor, onDeactivateVendor }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const itemsPerPage = 10;
  // console.log(vendors);

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    // { value: "pending", label: "Pending" }
  ];

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
  statusFilter === "all" ||
  (statusFilter === "active" && vendor.active) ||
  (statusFilter === "inactive" && !vendor.active);
    return matchesSearch && matchesStatus;
  });

  const sortedVendors = [...filteredVendors].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'lastActivity') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedVendors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedVendors = sortedVendors.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectVendor = (vendorId) => {
    setSelectedVendors(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVendors.length === paginatedVendors.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(paginatedVendors.map(vendor => vendor.id));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success/10 text-success', label: 'Active' },
      inactive: { color: 'bg-muted text-muted-foreground', label: 'Inactive' },
      // pending: { color: 'bg-warning/10 text-warning', label: 'Pending' }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Table Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Vendor Management</h2>
            <p className="text-sm text-muted-foreground">Manage and monitor all vendor accounts</p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Input
              type="search"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sm:w-64"
            />
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
              className="sm:w-40"
            />
          </div>
        </div>
        
        {selectedVendors.length > 0 && (
          <div className="mt-4 flex items-center justify-between p-3 bg-muted/50 rounded-md">
            <span className="text-sm text-foreground">
              {selectedVendors.length} vendor{selectedVendors.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                <Icon name="Mail" size={16} />
                Send Email
              </Button>
              <Button variant="outline" size="sm">
                <Icon name="Download" size={16} />
                Export
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="w-12 p-4">
                <input
                  type="checkbox"
                  checked={selectedVendors.length === paginatedVendors.length && paginatedVendors.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary micro-animation"
                >
                  <span>Vendor</span>
                  <Icon 
                    name={sortField === 'name' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('employeeCount')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary micro-animation"
                >
                  <span>Employees</span>
                  <Icon 
                    name={sortField === 'employeeCount' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-foreground">Status</span>
              </th>
              <th className="text-left p-4">
                <button
               
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary micro-animation"
                >
                  <span>Total Earnings</span>
                 
                </button>
              </th>
              <th className="text-right p-4">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedVendors.map((vendor) => (
              <tr 
                key={vendor.id} 
                className="border-b border-border hover:bg-muted/30 micro-animation cursor-pointer"
                onClick={() => onViewVendor(vendor)}
              >
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedVendors.includes(vendor._id)}
                    onChange={() => handleSelectVendor(vendor._id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Building2" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{vendor.name}</p>
                      <p className="text-sm text-muted-foreground">{vendor.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{vendor.employeeCount}</span>
                </td>
                <td className="p-4">
                  <span className={`text-sm text-foreground ${vendor.active ? 'text-success' : 'text-muted-foreground'}`}>{vendor.active ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="p-4 text-right">
                  <span className="text-sm text-muted-foreground " >{vendor?.totalEmployeeSales?.toFixed(2)}</span>
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end space-x-2">
                
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditVendor(vendor)}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    {vendor?.active ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeactivateVendor(vendor,'delete')}
                      className="text-destructive hover:text-destructive"
                    >
                      <Icon name="UserX" size={16} />
                    </Button>
                    ) : (
                     <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeactivateVendor(vendor,'active')}
                      className="text-lime-600 hover:text-lime-600"
                    >
                      <Icon name="UserCheck" size={16} />
                    </Button>
                    
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden p-4 space-y-4">
        {paginatedVendors.map((vendor) => (
          <div key={vendor.id} className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedVendors.includes(vendor?._id)}
                  onChange={() => handleSelectVendor(vendor?._id)}
                  className="rounded border-border"
                />
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Building2" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{vendor.name}</p>
                  <p className="text-sm text-muted-foreground">{vendor.email}</p>
                </div>
              </div>
              {getStatusBadge(vendor?.active ? 'active' : 'inactive')}
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Employees:</span>
                <span className="ml-2 text-foreground">{vendor?.employeeCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Earnings:</span>
                <span className="ml-2 text-foreground">{vendor?.totalEmployeeSales?.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="flex space-x-2 pt-2 border-t border-border">
             
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => onEditVendor(vendor)}
                iconName="Edit"
                iconPosition="left"
                iconSize={16}
              >
                Edit
              </Button>
            {
              vendor?.active ? (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => onDeactivateVendor(vendor,'delete')}
                className="text-destructive hover:text-destructive"
                iconName="UserX"
                iconPosition="left"
                iconSize={16}
              >
                Deactivate
              </Button>
              ) : (
               <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => onDeactivateVendor(vendor,'active')}
                className="text-lime-600 hover:text-lime-600"
                iconName="UserCheck"
                iconPosition="left"
                iconSize={16}
              >
                Activate
              </Button>)
            }
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedVendors.length)} of {sortedVendors.length} vendors
            </p>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                iconName="ChevronLeft"
                iconPosition="left"
                iconSize={16}
              >
                Previous
              </Button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="w-8 h-8"
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                iconName="ChevronRight"
                iconPosition="right"
                iconSize={16}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorTable;